import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, Building2, Home, TestTube2, Phone, User, XCircle, FileText, Receipt, ArrowLeft, Clock, MapPin } from 'lucide-react';
import { patientApi, type LabHomeCollection } from '../api';

const TIMELINE_STEPS = ['REQUESTED', 'ACCEPTED', 'SCHEDULED', 'SAMPLE_COLLECTED', 'IN_PROGRESS', 'COMPLETED', 'REPORT_READY'];
const STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'bg-yellow-100 text-yellow-700',
  ACCEPTED: 'bg-blue-100 text-blue-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  SAMPLE_COLLECTED: 'bg-indigo-100 text-indigo-700',
  IN_PROGRESS: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REPORT_READY: 'bg-green-100 text-green-700',
};

export default function PatientLabAppointmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [apt, setApt] = useState<any>(null);
  const [liveStatus, setLiveStatus] = useState<LabHomeCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!id) return;
    patientApi.lab.getAppointment(id)
      .then((d: any) => { if (alive) setApt(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  // The lab's real home-collection visit for this booking. The list is keyed
  // by the booking's own number, which is how the two records line up.
  useEffect(() => {
    let alive = true;
    if (!apt || apt.collectionType !== 'HOME_COLLECTION') return;
    patientApi.lab.getHomeCollections()
      .then((rows) => {
        if (!alive) return;
        const match = (rows ?? []).find(r =>
          (r.orderNumber && r.orderNumber === apt.appointmentNumber) || r.id === id,
        );
        setLiveStatus(match ?? null);
      })
      .catch(() => { if (alive) setLiveStatus(null); });
    return () => { alive = false; };
  }, [apt, id]);

  const canCancel = apt && ['REQUESTED', 'ACCEPTED', 'SCHEDULED'].includes(apt.status);

  const handleCancel = async () => {
    if (!id || !confirm('Cancel this appointment?')) return;
    setCancelling(true);
    try {
      await patientApi.lab.cancelAppointment(id);
      setApt((p: any) => ({ ...p, status: 'CANCELLED' }));
    } catch { alert('Failed to cancel'); }
    finally { setCancelling(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse"><div className="h-5 bg-gray-200 rounded w-1/2 mb-2" /><div className="h-4 bg-gray-100 rounded w-1/3" /></div>)}
      </div>
    );
  }

  if (!apt) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Appointment not found</p>
      </div>
    );
  }

  const status = apt.status || 'REQUESTED';
  const stepIndex = TIMELINE_STEPS.indexOf(status);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate('/patient/lab-appointments')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Back to Appointments
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{apt.appointmentNumber || apt.id}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                {status.replace(/_/g, ' ')}
              </span>
            </div>
            <h1 className="text-lg font-bold text-gray-900">{apt.labName || 'Lab Appointment'}</h1>
            <p className="text-sm text-gray-500 mt-1">Lab services on NeoHealthCard</p>
          </div>
          {canCancel && (
            <button onClick={handleCancel} disabled={cancelling} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
              <XCircle className="w-3.5 h-3.5" /> {cancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{apt.date || apt.slotDate || '—'}</span>
          <span className="flex items-center gap-1">
            {apt.collectionType === 'HOME_COLLECTION' ? <Home className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
            {apt.collectionType === 'HOME_COLLECTION' ? 'Home Collection' : 'Lab Visit'}
          </span>
        </div>
      </div>

      {/* Status Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Status Timeline</h2>
        <div className="space-y-0">
          {TIMELINE_STEPS.map((s, i) => {
            const done = i <= stepIndex;
            const current = i === stepIndex;
            return (
              <div key={s} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${
                    done ? 'bg-teal-600 border-teal-600' : 'bg-white border-gray-300'
                  } ${current ? 'ring-2 ring-teal-200' : ''}`} />
                  {i < TIMELINE_STEPS.length - 1 && <div className={`w-0.5 h-6 ${done ? 'bg-teal-400' : 'bg-gray-200'}`} />}
                </div>
                <span className={`text-xs pb-4 ${done ? 'text-teal-700 font-medium' : 'text-gray-400'}`}>
                  {s.replace(/_/g, ' ')}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tests */}
      {apt.tests && apt.tests.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Tests</h2>
          <div className="space-y-2">
            {apt.tests.map((t: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <TestTube2 className="w-4 h-4 text-teal-500 shrink-0" />
                <span className="text-gray-800">{typeof t === 'string' ? t : t.testName || t.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Home collection — the lab's own visit record */}
      {apt.collectionType === 'HOME_COLLECTION' && (
        <div className="bg-white rounded-xl border border-teal-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Home collection</h2>
          {!liveStatus ? (
            <p className="text-xs text-gray-500 leading-relaxed">
              The lab has not scheduled your visit yet. Once a phlebotomist is assigned, their name and the
              visit status appear here.
            </p>
          ) : (
            <div className="space-y-3 text-sm">
              {liveStatus.phlebotomistName && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    {liveStatus.phlebotomistName}
                    {liveStatus.phlebotomistNhcId && (
                      <span className="text-gray-400"> · {liveStatus.phlebotomistNhcId}</span>
                    )}
                  </span>
                </div>
              )}
              {liveStatus.scheduledAt && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    {new Date(liveStatus.scheduledAt).toLocaleString('en-IN', {
                      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
                    })}
                  </span>
                </div>
              )}
              {liveStatus.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span className="text-gray-700 text-xs leading-relaxed">
                    {liveStatus.address}{liveStatus.pincode ? ` — ${liveStatus.pincode}` : ''}
                  </span>
                </div>
              )}
              <div className="px-3 py-2 rounded-lg bg-teal-50 text-teal-700 text-xs font-medium">
                {liveStatus.status.replace(/_/g, ' ')}
                {liveStatus.patientVerified ? ' · identity verified at your door' : ''}
              </div>
              {liveStatus.failReason && (
                <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{liveStatus.failReason}</div>
              )}
              {liveStatus.timeline?.length > 0 && (
                <div className="space-y-1 pt-1 border-t border-gray-100">
                  {liveStatus.timeline.map((u) => (
                    <p key={`${u.status}-${u.at}`} className="text-xs text-gray-500">
                      {u.note || u.status.replace(/_/g, ' ')}
                      {u.at && ` — ${new Date(u.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}`}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Links */}
      <div className="flex items-center gap-3 flex-wrap">
        {apt.orderId && (
          <button onClick={() => navigate(`/patient/lab-invoices`)} className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50">
            <Receipt className="w-3.5 h-3.5" /> View Invoice
          </button>
        )}
        {apt.reportId && (
          <button onClick={() => navigate(`/patient/lab-reports`)} className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50">
            <FileText className="w-3.5 h-3.5" /> View Report
          </button>
        )}
      </div>
    </div>
  );
}
